import { useEffect, useRef, useState } from 'react';

/**
 * Publicações oficiais (organizadores e participantes) sobre eventos atendidos pela STIB,
 * incorporadas com o embed oficial do Instagram. O conteúdo continua hospedado no Instagram,
 * com o nome do autor. Para incluir: acrescente { path: 'p/CODIGO' ou 'reel/CODIGO', autor, evento }.
 */
type Post = { path: string; autor: string; evento: string };

const POSTS: Post[] = [
  { path: 'reel/DemwPJ4y-Xk', autor: 'soea_oficial', evento: '81ª SOEA' },
  { path: 'reel/DepmWCbJc8O', autor: 'soea_oficial', evento: '81ª SOEA' },
  { path: 'reel/Deo9HFiJ5lc', autor: 'soea_oficial', evento: '81ª SOEA' },
  { path: 'p/Defnx8hylKq', autor: 'confea_', evento: '81ª SOEA' },
  { path: 'p/Derdj6AFhma', autor: 'creadf', evento: '81ª SOEA' },
  { path: 'p/DekZSNCF02L', autor: 'creaparana', evento: '81ª SOEA' },
  { path: 'p/Deg-yCeoMan', autor: 'pediatriaemquestao', evento: 'Congresso Brasileiro de Pediatria' },
  { path: 'p/DenOox7mF6f', autor: 'resipedamapa', evento: 'Congresso Brasileiro de Pediatria' },
  { path: 'p/DekPH1LCTf5', autor: 'anahp.br', evento: 'CONAHP 2026' },
  { path: 'p/DekAYvsiXkn', autor: 'anahp.br', evento: 'CONAHP 2026' },
  { path: 'reel/DeiIRKNCouK', autor: 'anahp.br', evento: 'CONAHP 2026' },
  { path: 'p/Deh71qxDXGc', autor: 'anahp.br', evento: 'CONAHP 2026' },
  { path: 'p/Deh1aMSiQVJ', autor: 'anahp.br', evento: 'CONAHP 2026' },
  { path: 'p/DehupwqCVnc', autor: 'anahp.br', evento: 'CONAHP 2026' },
  { path: 'p/DaObgOtRMOo', autor: 'saer_pcba', evento: 'ENAVSEG 2026' },
  { path: 'p/DaTgc-4Gjbz', autor: 'notaer.es', evento: 'ENAVSEG 2026' },
  { path: 'p/DbMJ8pXiZIu', autor: 'maxvendingmachine', evento: 'Fitness Brasil Expo 2025' },
  { path: 'p/Dbsi70dE4Lu', autor: 'mormaiifranquias', evento: 'Fitness Brasil Expo 2025' },
];

const EVENTOS = ['Todos', ...Array.from(new Set(POSTS.map((p) => p.evento)))];
const INICIAL = 6;

export default function PostsOficiais() {
  const [evento, setEvento] = useState('Todos');
  const [mostrar, setMostrar] = useState(INICIAL);
  const frames = useRef<Map<string, HTMLIFrameElement>>(new Map());
  const lista = POSTS.filter((p) => evento === 'Todos' || p.evento === evento);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      try { if (!/instagram\.com$/.test(new URL(e.origin).hostname)) return; } catch { return; }
      let d: any = e.data;
      if (typeof d === 'string') { try { d = JSON.parse(d); } catch { return; } }
      if (!d || d.type !== 'MEASURE' || !d.details?.height) return;
      frames.current.forEach((f) => { if (f.contentWindow === e.source) f.style.height = `${d.details.height}px`; });
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <section id="posts-oficiais" className="py-20 bg-gray-50 dark:bg-gray-800/50 scroll-mt-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3">Nos canais oficiais</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary dark:text-white mb-4">Os eventos pelo olhar de quem organizou e participou</h2>
          <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Publicações dos organizadores e participantes de eventos em que a STIB forneceu fones sem fio e equipe técnica.
            Conteúdo incorporado do Instagram, com os créditos de cada autor.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {EVENTOS.map((ev) => (
              <button
                key={ev}
                type="button"
                onClick={() => { setEvento(ev); setMostrar(INICIAL); }}
                className={`font-body text-sm font-semibold px-5 py-2.5 rounded-full border transition-colors ${evento === ev ? 'bg-primary text-secondary border-primary' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary'}`}
              >
                {ev}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {lista.slice(0, mostrar).map((p) => (
            <figure key={p.path} className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
              <iframe
                ref={(el) => { if (el) frames.current.set(p.path, el); else frames.current.delete(p.path); }}
                src={`https://www.instagram.com/${p.path}/embed/`}
                title={`${p.evento}: publicação de @${p.autor}`}
                loading="lazy"
                allow="encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                scrolling="no"
                className="w-full block border-0 bg-white"
                style={{ height: 620 }}
              />
              <figcaption className="font-body text-sm text-gray-700 dark:text-gray-300 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                <strong className="text-secondary dark:text-white">{p.evento}</strong> · publicação de @{p.autor}
              </figcaption>
            </figure>
          ))}
        </div>

        {mostrar < lista.length && (
          <div className="text-center mt-10">
            <button
              type="button"
              onClick={() => setMostrar((m) => m + INICIAL)}
              className="font-body font-semibold bg-secondary text-white px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity"
            >
              Ver mais publicações ({lista.length - mostrar})
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
